import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Box, Button, CircularProgress, Drawer, Fab, IconButton, List, ListItemButton, ListItemText, Snackbar, Typography } from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import api from '../Services/api';
import { conectarNotificaciones } from '../Services/realtime';
import '../Styles/NotificacionesReportes.css';

const etiquetas = { asignacion: 'Asignación', ticket_creado: 'Nuevo reporte', comentario_nuevo: 'Comentario', cambio_estado: 'Cambio de estado' };
const estaLeida = n => n.leido === true || n.leido === 1 || n.leido === '1';

export default function NotificacionesReportes({ onCountChange }) {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [nueva, setNueva] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const idsConocidos = useRef(null);
  const activo = useRef(false);
  const consulta = useRef(false);

  const cargar = useCallback(async () => {
    if (!localStorage.getItem('token') || consulta.current) return;
    consulta.current = true;
    try {
      const { data } = await api.get('/notifications', { timeout: 8000 });
      if (!activo.current) return;
      const lista = Array.isArray(data.notificaciones) ? data.notificaciones : [];
      const recibidas = idsConocidos.current && lista.filter(n => !idsConocidos.current.has(String(n.id_notif)) && !estaLeida(n));
      if (recibidas?.length) setNueva({ ...recibidas[0], adicionales: recibidas.length - 1 });
      idsConocidos.current = new Set(lista.map(n => String(n.id_notif)));
      setNotificaciones(lista);
      const cantidad = Number(data.no_leidas) || 0;
      setNoLeidas(cantidad);
      onCountChange(cantidad);
      setError('');
    } catch (err) {
      if (activo.current) {
        const status = err.response?.status;
        setError(status === 401
          ? 'El servicio de Reportes no aceptó tu sesión. Vuelve a iniciar sesión.'
          : status === 403
            ? 'Tu usuario no tiene acceso a las notificaciones de Reportes.'
            : 'No se pudieron cargar las notificaciones. Intenta nuevamente.');
      }
    } finally {
      consulta.current = false;
      if (activo.current) setCargando(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    activo.current = true;
    cargar();
    const socket = conectarNotificaciones();
    let temporizador;
    const actualizar = () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(cargar, 100);
    };
    socket.on('notifications:new', actualizar);
    socket.on('connect', cargar);
    const alVolver = () => { if (document.visibilityState === 'visible') cargar(); };
    // También concilia lecturas realizadas desde el módulo embebido.
    const intervalo = setInterval(alVolver, 30000);
    window.addEventListener('focus', alVolver);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      activo.current = false;
      clearTimeout(temporizador);
      clearInterval(intervalo);
      socket.disconnect();
      window.removeEventListener('focus', alVolver);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [cargar]);

  const abrirNotificacion = async n => {
    try {
      if (!estaLeida(n)) await api.patch(`/notifications/${encodeURIComponent(n.id_notif)}/read`);
    } catch {
      if (activo.current) setError('No se pudo marcar la notificación como leída.');
    }
    if (!activo.current) return;
    setAbierto(false);
    setNueva(null);
    // Abre el reporte en el mismo servicio embebido que usa la lista.
    navigate(`/reportes?reporte=${encodeURIComponent(n.id_report)}`);
    cargar();
  };

  const marcarTodas = async () => {
    setGuardando(true);
    try {
      await api.patch('/notifications/read-all');
      await cargar();
    } catch {
      if (activo.current) setError('No se pudieron marcar las notificaciones como leídas.');
    } finally {
      if (activo.current) setGuardando(false);
    }
  };

  return <>
    <Fab color="primary" size="medium" onClick={() => { setAbierto(true); cargar(); }}
      aria-label={`Notificaciones de reportes${noLeidas ? `, ${noLeidas} sin leer` : ''}`}
      sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: theme => theme.zIndex.drawer - 1 }}>
      <Badge badgeContent={noLeidas} color="error" max={99}>
        <NotificationsOutlinedIcon key={nueva?.id_notif} className={nueva ? 'reportes-notification-bell-ring' : ''} />
      </Badge>
    </Fab>
    <Drawer anchor="right" open={abierto} onClose={() => setAbierto(false)}
      PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-label': 'Notificaciones de reportes', sx: { width: { xs: '100%', sm: 390 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <NotificationsOutlinedIcon />
        <Typography fontWeight={700} sx={{ flex: 1 }}>Notificaciones</Typography>
        <IconButton color="inherit" aria-label="Cerrar notificaciones" onClick={() => setAbierto(false)}><CloseIcon /></IconButton>
      </Box>
      {noLeidas > 0 && <Button startIcon={<DoneAllIcon />} disabled={guardando} onClick={marcarTodas} sx={{ m: 1 }}>Marcar todas como leídas</Button>}
      {error && <Alert severity="error" action={<Button color="inherit" onClick={cargar}>Reintentar</Button>} sx={{ m: 1 }}>{error}</Alert>}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {cargando ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} aria-label="Cargando notificaciones" /></Box>
          : !error && !notificaciones.length ? <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No tienes notificaciones</Typography>
          : <List disablePadding>{notificaciones.map(n => <ListItemButton key={n.id_notif} onClick={() => abrirNotificacion(n)}
            sx={{ alignItems: 'flex-start', borderBottom: 1, borderColor: 'divider', bgcolor: estaLeida(n) ? 'background.paper' : 'action.selected' }}>
            <ListItemText primary={n.mensaje} primaryTypographyProps={{ fontWeight: estaLeida(n) ? 400 : 700 }} secondary={<>
              {etiquetas[n.tipo] || 'Reporte'}{n.titulo ? ` · ${n.titulo}` : ''}<br />
              {n.created_at && new Date(n.created_at).toLocaleString('es-CO')}
            </>} />
            {!estaLeida(n) && <Box component="span" aria-label="No leída" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 2, ml: 1 }} />}
          </ListItemButton>)}</List>}
      </Box>
      <Typography sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider', color: 'text.secondary' }}>{noLeidas ? `${noLeidas} sin leer` : 'Todo al día'}</Typography>
    </Drawer>
    <Snackbar key={nueva?.id_notif || 'sin-aviso'} open={Boolean(nueva)} autoHideDuration={8000} onClose={(_, reason) => { if (reason !== 'clickaway') setNueva(null); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Alert severity="info" variant="filled" onClose={() => setNueva(null)} className="reportes-notification-toast-enter"
        action={<><Button color="inherit" size="small" onClick={() => abrirNotificacion(nueva)}>Ver reporte</Button><IconButton color="inherit" size="small" aria-label="Cerrar aviso" onClick={() => setNueva(null)}><CloseIcon fontSize="small" /></IconButton></>}>
        <Typography variant="subtitle2">Nueva notificación{nueva?.adicionales > 0 ? ` · +${nueva.adicionales}` : ''}</Typography>
        {nueva?.mensaje}
      </Alert>
    </Snackbar>
  </>;
}
